import { motion } from 'framer-motion';
import { Target, Zap, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AnimateIn from '../components/AnimateIn';

const About = () => {
  const { t } = useLanguage();

  const stagger = {
    animate: { transition: { staggerChildren: 0.15 } },
  };
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 lg:px-8 overflow-hidden">
        <div
          className="absolute -top-24 right-0 w-[480px] h-[480px] rounded-full blur-[150px] opacity-[0.12] pointer-events-none"
          style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}
        />
        <div className="relative z-10 max-w-[1200px] mx-auto">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.p
              className="accent-tag"
              variants={fadeInUp}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t('aboutTag')}
            </motion.p>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] tracking-[-0.02em] text-white max-w-3xl mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="about-title"
            >
              {t('aboutTitle')}
            </motion.h1>

            <motion.p
              className="text-base md:text-lg leading-[1.8] max-w-2xl text-gray-400"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="about-vision"
            >
              {t('aboutVision')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 md:py-28 px-6 lg:px-8" style={{ background: '#0a0a0a' }} data-testid="who-we-are-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <AnimateIn direction="left">
              <p className="accent-tag">{t('whoWeAreTag')}</p>
              <h2
                className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white mb-6"
                style={{ fontFamily: "'Syne', sans-serif" }}
                data-testid="who-we-are-title"
              >
                {t('whoWeAreTitle')}
              </h2>
              <p
                className="text-base leading-[1.8] text-gray-400"
                data-testid="who-we-are-text"
              >
                {t('whoWeAreText')}
              </p>
            </AnimateIn>

            <div className="space-y-6">
              {[
                {
                  icon: Target,
                  title: 'Strategic Alignment',
                  desc: 'Every platform in the CEXUS network is built to lift the others, sharing audiences, tools and momentum.',
                },
                {
                  icon: Zap,
                  title: 'Scalable Systems',
                  desc: 'Infrastructure that grows with you, staying fast and reliable from launch day to your busiest season.',
                },
                {
                  icon: Shield,
                  title: 'Independent Operations',
                  desc: 'Each brand runs on its own terms while tapping into the shared resources of the CEXUS network.',
                },
              ].map((item, i) => (
                <AnimateIn key={i} delay={i * 0.12} direction="right">
                  <div
                    className="flex gap-5 p-6 rounded-2xl bg-white/[0.03] border border-white/10 transition-all hover:border-white/20 hover:-translate-y-0.5"
                    data-testid={`about-feature-${i}`}
                  >
                    <item.icon
                      size={24}
                      strokeWidth={1.5}
                      className="shrink-0 mt-0.5"
                      style={{ color: '#e2c179' }}
                    />
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1.5" style={{ fontFamily: "'Syne', sans-serif" }}>
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-400">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="mission-section">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn className="max-w-3xl mx-auto text-center">
            <p className="accent-tag justify-center">{t('missionTag')}</p>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
              data-testid="mission-title"
            >
              {t('missionTitle')}
            </h2>
            <p
              className="text-base md:text-lg leading-[1.8] text-gray-400"
              data-testid="mission-text"
            >
              {t('missionText')}
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* Bottom accent */}
      <AnimateIn direction="none">
        <div className="py-12 px-6 text-center border-y border-white/[0.06]" style={{ background: 'rgba(226,193,121,0.05)' }}>
          <p className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#e2c179' }}>
            {t('beyondReality')}
          </p>
        </div>
      </AnimateIn>
    </div>
  );
};

export default About;
